import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from "react-router-dom";
import Accumulus from "components/Accumulus";
import { Guard, SecureRoute } from "components/common/Auth";
import * as accessChecks from "components/common/Auth/accessChecks";
import { LegalAcknowledgementGuard } from "components/common/Auth/Guard";
import EntryLayout from "components/common/Layouts/EntryLayout";
import MainLayout from "components/common/Layouts/MainLayout";
import Redirect from "components/common/Redirect";
import { ConfigurationsProvider } from "context/ConfigurationsContext";
import { FeatureFlagsProvider } from "context/FeatureFlagsContext";
import { LegalAcknowledgementProvider } from "context/LegalAcknowledgementContext";
import ProjectProvider from "context/ProjectContext/ProjectContext";
import StructuredDocumentProvider from "context/StructuredDocumentContext/StructuredDocumentContext";
import StructuredTemplateProvider from "context/StructuredTemplateContext/StructuredTemplateContext";
import { UserProvider } from "context/UserContext";
import { Path, RelativePath } from "utils/constants/paths.constants";

export const createRouter = () =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route element={<Accumulus />}>
        <Route element={<EntryLayout />}>
          <Route path={Path.Login} lazy={() => import("screens/Login")} />
        </Route>
        <Route
          path={Path.Registration}
          lazy={() => import("screens/Registration")}
        >
          <Route
            index
            lazy={() =>
              import("screens/Registration/components/RegistrationForm")
            }
          />
          <Route
            path={Path.RegistrationConfirmation}
            lazy={() =>
              import("screens/Registration/components/RegistrationConfirmation")
            }
          />
        </Route>
        <Route element={<SecureRoute />}>
          <Route element={<LegalAcknowledgementProvider />}>
            <Route
              element={
                <LegalAcknowledgementGuard
                  check={accessChecks.hasLegalTermsToAccept}
                  onDenied={() => <Navigate to={Path.HomePage} replace />}
                />
              }
            >
              <Route element={<EntryLayout />}>
                <Route
                  path={Path.LegalAcknowledgementsConsent}
                  lazy={() => import("screens/LegalAcknowledgements/Consent")}
                />
                <Route
                  path={Path.LegalAcknowledgementDecline}
                  lazy={() => import("screens/LegalAcknowledgements/Decline")}
                />
              </Route>
            </Route>
            <Route
              element={
                <LegalAcknowledgementGuard
                  check={accessChecks.hasAcceptedLegalTerms}
                  onDenied={() => (
                    <Navigate to={Path.LegalAcknowledgementsConsent} replace />
                  )}
                />
              }
            >
              <Route element={<FeatureFlagsProvider />}>
                <Route element={<UserProvider />}>
                  <Route path={Path.HomePage} element={<MainLayout />}>
                    <Route
                      element={
                        <Guard
                          check={accessChecks.canAccessApp}
                          onDenied={() => <Redirect path={Path.Admin} />}
                        />
                      }
                    >
                      <Route index lazy={() => import("screens/Homepage")} />
                      <Route
                        path={Path.Projects}
                        lazy={() => import("screens/Projects")}
                      />

                      <Route element={<ProjectProvider />}>
                        <Route
                          path={RelativePath.Project}
                          lazy={() => import("screens/Project")}
                        />
                        <Route
                          path={Path.ProjectHAQ}
                          lazy={() => import("screens/Project/screens/HAQ")}
                        />
                        <Route
                          path={Path.ProjectECTD}
                          lazy={() => import("screens/Project/screens/ECTD")}
                        />

                        <Route element={<StructuredDocumentProvider />}>
                          <Route
                            path={Path.ProjectStructuredDocument}
                            lazy={() =>
                              import(
                                "screens/Project/screens/StructuredDocument"
                              )
                            }
                          />

                          <Route
                            path={Path.ProjectStructuredDocumentManagement}
                            lazy={() =>
                              import(
                                "screens/Project/screens/StructuredDocument/Management"
                              )
                            }
                          />
                        </Route>
                        <Route element={<StructuredTemplateProvider />}>
                          <Route
                            path={Path.ProjectStructuredTemplateManagement}
                            lazy={() =>
                              import(
                                "screens/Project/screens/StructuredTemplate/Management"
                              )
                            }
                          ></Route>
                        </Route>
                        <Route
                          path={Path.ProjectDocument}
                          lazy={() => import("screens/Project/screens/PDF")}
                        />
                      </Route>

                      <Route
                        path={Path.Tasks}
                        lazy={() => import("screens/Tasks")}
                      />

                      <Route
                        path={Path.Task}
                        lazy={() => import("screens/Tasks")}
                      />
                    </Route>

                    <Route path={Path.Admin}>
                      <Route
                        element={
                          <Guard check={accessChecks.canAccessAdminPages} />
                        }
                      >
                        <Route
                          element={
                            <Guard
                              check={accessChecks.canAccessUserManagement}
                            />
                          }
                        >
                          <Route
                            index
                            element={
                              <Redirect path={Path.AdminUserManagement} />
                            }
                          />

                          <Route
                            path={Path.AdminUserManagement}
                            lazy={() =>
                              import("screens/Administration/UserManagement")
                            }
                          />

                          <Route
                            path={Path.AdminAddNewUser}
                            lazy={() =>
                              import(
                                "screens/Administration/components/AddUser"
                              )
                            }
                          />

                          <Route
                            path={Path.AdminUser}
                            lazy={() =>
                              import("screens/Administration/components/User")
                            }
                          >
                            <Route
                              index
                              element={
                                <Redirect path={Path.AdminUserProfile} />
                              }
                            />
                            <Route
                              path={Path.AdminUserProfile}
                              lazy={() =>
                                import(
                                  "screens/Administration/components/User/Profile"
                                )
                              }
                            />
                          </Route>
                        </Route>
                        <Route
                          element={<Guard check={accessChecks.canAccessCCS} />}
                        >
                          <Route element={<ConfigurationsProvider />}>
                            <Route
                              path={Path.AdminTenants}
                              lazy={() =>
                                import("screens/Administration/Tenants")
                              }
                            />
                            <Route
                              path={Path.AdminFeatureFlags}
                              lazy={() =>
                                import("screens/Administration/FeatureFlags")
                              }
                            />
                          </Route>
                        </Route>
                      </Route>
                    </Route>

                    <Route
                      path={Path.Profile}
                      lazy={() => import("screens/Profile")}
                    />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" lazy={() => import("screens/PageNotFound")} />
      </Route>,
    ),
  );
